import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { firstValueFrom } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { FaqHelp } from '../domain/faq-help';

export class FaqHelpSchema {
  target: string;
  title: string;
  content: string;
  sort: number;
}

@Injectable()
export class FaqHelpRepository {
  constructor(private firestore: AngularFirestore) {
    // do nothing
  }

  private collection(queryFn?: QueryFn) {
    return this.firestore.collection<FaqHelpSchema>('faq_helps', queryFn);
  }

  public toSchema(entity: FaqHelp): FaqHelpSchema {
    return <FaqHelpSchema>{
      target: entity.target,
      title: entity.title,
      content: entity.content,
      sort: Number(entity.sort ?? 0),
    };
  }

  public fromSchema(schema: FaqHelpSchema, id?: string): FaqHelp {
    return FaqHelp.create(
      {
        target: schema.target,
        title: schema.title,
        content: schema.content,
        sort: schema.sort.toString(),
      },
      new UniqueEntityID(id),
    );
  }

  public async findAllByTarget(target: string): Promise<FaqHelp[]> {
    try {
      const rs = await firstValueFrom(
        this.collection((ref) =>
          ref.where('target', '==', target).orderBy('sort', 'asc'),
        ).get(),
      );
      return rs.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
    } catch (e) {
      console.error('🔴 FaqHelpRepository.findAllByTarget', e);
      throw e;
    }
  }

  async findAll(target: string | undefined, lastId: string | undefined) {
    if (lastId) {
      const last = await firstValueFrom(this.collection().doc(lastId).get());
      const snap = await firstValueFrom(
        this.collection((ref) => {
          if (target) {
            let qry = ref.where('target', '==', target);
            qry = qry.orderBy('sort', 'asc').startAfter(last).limit(30);
            return qry;
          } else {
            return ref
              .orderBy('target', 'asc')
              .orderBy('sort', 'asc')
              .startAfter(last)
              .limit(30);
          }
        }).get(),
      );
      return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
    } else {
      try {
        const snap = await firstValueFrom(
          this.collection((ref) => {
            if (target) {
              let qry = ref.where('target', '==', target);
              qry = qry.orderBy('sort', 'asc').limit(30);
              return qry;
            } else {
              return ref
                .orderBy('target', 'asc')
                .orderBy('sort', 'asc')
                .limit(30);
            }
          }).get(),
        );
        return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
      } catch (e) {
        console.log('❌', e);
        return [];
      }
    }
  }

  async create(faq: FaqHelp) {
    const schema = this.toSchema(faq);
    const inserted = await this.collection().add(schema);
    return this.fromSchema(schema, inserted.id);
  }

  async update(faq: FaqHelp) {
    const schema = this.toSchema(faq);
    await this.collection().doc(faq.id.toString()).set(schema);
    return faq;
  }

  async delete(faq: FaqHelp) {
    await this.collection().doc(faq.id.toString()).delete();
  }
}
